import { useCallback, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import { CaptureSheet, getLastCaptureMode, type CaptureMode } from '@/components/capture/CaptureSheet';

// Centre Capture FAB (P0-IA-02). The FAB now opens the real four-mode CaptureSheet (record / memo /
// snap [stubbed] / link). Tap = open the mode menu; long-press = jump straight to the last-used mode
// (persisted by CaptureSheet via @capacitor/preferences). The sheet itself renders through a portal
// to document.body (fixed inset-0 resolves against the viewport), so the FAB only owns the button +
// the open/initial-mode state here.
const LONG_PRESS_MS = 450;

export function CaptureFab() {
  const [open, setOpen] = useState(false);
  const [initialMode, setInitialMode] = useState<CaptureMode | null>(null);

  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFired = useRef(false);

  const openMenu = useCallback(() => {
    setInitialMode(null);
    setOpen(true);
  }, []);

  const openLastMode = useCallback(async () => {
    const last = await getLastCaptureMode();
    setInitialMode(last);
    setOpen(true);
  }, []);

  const startPress = useCallback(() => {
    longPressFired.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      void openLastMode();
    }, LONG_PRESS_MS);
  }, [openLastMode]);

  const cancelPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleClick = useCallback(() => {
    // If the long-press already fired, the press handler opened last-mode; swallow the click.
    if (longPressFired.current) {
      longPressFired.current = false;
      return;
    }
    openMenu();
  }, [openMenu]);

  return (
    <>
      <button
        type="button"
        aria-label="Capture"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={handleClick}
        onPointerDown={startPress}
        onPointerUp={cancelPress}
        onPointerLeave={cancelPress}
        onPointerCancel={cancelPress}
        onContextMenu={(e) => e.preventDefault()}
        className="flex h-14 w-14 -mt-6 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 ring-4 ring-background transition-transform active:scale-95"
      >
        <Plus className="h-7 w-7" />
      </button>
      <CaptureSheet open={open} onClose={() => setOpen(false)} initialMode={initialMode} />
    </>
  );
}
